/**
 * Deterministic text fitting for the shareable poster.
 *
 * ## Why this exists
 *
 * The poster asked for `adjustsFontSizeToFit` with a `minimumFontScale`, and on
 * iOS that works. **React Native Web does not implement the prop at all** —
 * verified against the installed source, where `adjustsFontSizeToFit` appears
 * zero times in `react-native-web/dist/exports/Text`. It is not warned about
 * and it does not throw; it is simply ignored.
 *
 * So on web the text never shrank. It stayed at its authored size, overflowed
 * its container, and was then clipped by the ONE part of the pair that RNW does
 * implement: `numberOfLines`, which becomes `text-overflow` for a single line
 * and `display: -webkit-box` + `-webkit-line-clamp` for more. A layout designed
 * around shrink-to-fit instead got cut off.
 *
 * The fix is not to find a web equivalent of the prop. It is to stop needing
 * one: a size computed from the content is the same number on every platform,
 * in a `<canvas>` capture as much as in a live DOM, and can be tested without
 * a device.
 *
 * ## Why not measure the real text
 *
 * Measuring would be more precise and is the wrong trade here. The poster is
 * captured through html2canvas into a detached clone, where measurement APIs
 * see a different layout context than the live document — which is the class of
 * difference that produced the original bug. A pure function of the string is
 * identical in both, which is the property that actually matters.
 */

/** Never smaller than this fraction of the authored size, whatever the text. */
export const MIN_FIT_SCALE = 0.5;

/**
 * Shrink `base` so `text` fits a box sized for `comfortable` characters.
 *
 * Linear in the overflow rather than stepped: a cliff at some character count
 * makes "999" and "1000" look like different designs, and the counts either
 * side of a cliff are exactly the ones a poster shows.
 *
 * Returns whole pixels. Sub-pixel font sizes render inconsistently between a
 * live DOM and a canvas capture, and the difference is visible at 250px.
 */
export function fitFontSize(
  base: number,
  text: string,
  comfortable: number,
): number {
  if (!Number.isFinite(base) || base <= 0) return 0;
  const length = text.length;
  if (!Number.isFinite(comfortable) || comfortable <= 0) return Math.round(base);
  if (length <= comfortable) return Math.round(base);

  const scale = Math.max(MIN_FIT_SCALE, comfortable / length);
  return Math.round(base * scale);
}
